// A customer's statement (docs/plans/customer-statement.md): every bill, credit note and
// payment with a running balance, worked out on the device from `parties.customerLedger`.
// Nothing here is stored. Pure functions, unit-tested.
import { adToBs, formatBsDate } from '@ims/core'
import type { CustomerLedger, PaymentMethod } from '@ims/data'
import { daysBetween, type DateRange } from './reports.ts'

export type StatementKind = 'bill' | 'credit' | 'payment'

export interface StatementRow {
  kind: StatementKind
  id: string
  date: string
  /** The bill's or credit note's number; null for a payment (or before the number syncs). */
  number: string | null
  /** A payment's method, a credit note's reason. */
  method: PaymentMethod | null
  reference: string | null
  /** The bill a credit note was made against. */
  invoiceId: string | null
  invoiceNumber: string | null
  billedPaisa: number
  /** A payment or a credit note. */
  paidPaisa: number
  /** What is owed after this line. */
  balancePaisa: number
  /** The part of a payment set against no bill. */
  aheadPaisa: number
}

export interface Statement extends DateRange {
  /** Owed before `from`. */
  openingPaisa: number
  rows: StatementRow[]
  billedPaisa: number
  paidPaisa: number
  /** Owed at the end of `to`. */
  closingPaisa: number
}

const ORDER: Record<StatementKind, number> = { bill: 0, credit: 1, payment: 2 }
const day = (iso: string) => iso.slice(0, 10)

/** Every line of the ledger, oldest first (bills before credits before payments on a day). */
function lines(ledger: CustomerLedger): (StatementRow & { createdAt: string })[] {
  const out: (StatementRow & { createdAt: string })[] = []
  for (const b of ledger.bills)
    out.push({
      kind: 'bill',
      id: b.id,
      date: day(b.date),
      createdAt: b.createdAt,
      number: b.number,
      method: null,
      reference: null,
      invoiceId: b.id,
      invoiceNumber: b.number,
      billedPaisa: b.totalPaisa,
      paidPaisa: 0,
      balancePaisa: 0,
      aheadPaisa: 0,
    })
  for (const c of ledger.credits)
    out.push({
      kind: 'credit',
      id: c.id,
      date: day(c.date),
      createdAt: c.createdAt,
      number: c.number,
      method: null,
      reference: c.reason,
      invoiceId: c.invoiceId,
      invoiceNumber: c.invoiceNumber,
      billedPaisa: 0,
      paidPaisa: c.totalPaisa,
      balancePaisa: 0,
      aheadPaisa: 0,
    })
  for (const p of ledger.payments)
    out.push({
      kind: 'payment',
      id: p.id,
      date: day(p.date),
      createdAt: p.createdAt,
      number: null,
      method: p.method,
      reference: p.reference,
      invoiceId: null,
      invoiceNumber: null,
      billedPaisa: 0,
      paidPaisa: p.amountPaisa,
      balancePaisa: 0,
      aheadPaisa: paidAheadOf(p),
    })
  return out.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      ORDER[a.kind] - ORDER[b.kind] ||
      a.createdAt.localeCompare(b.createdAt),
  )
}

/** The part of a payment set against no bill that is still finalized. */
export function paidAheadOf(payment: CustomerLedger['payments'][number]): number {
  return Math.max(
    0,
    payment.amountPaisa - payment.allocations.reduce((sum, a) => sum + a.amountPaisa, 0),
  )
}

/** The statement for `range`: what was owed before it, every line in it, what is owed after. */
export function buildStatement(ledger: CustomerLedger, range: DateRange): Statement {
  let balance = 0
  let opening = 0
  let billed = 0
  let paid = 0
  const rows: StatementRow[] = []
  for (const { createdAt: _createdAt, ...line } of lines(ledger)) {
    if (line.date > range.to) break
    balance += line.billedPaisa - line.paidPaisa
    if (line.date < range.from) {
      opening = balance
      continue
    }
    billed += line.billedPaisa
    paid += line.paidPaisa
    rows.push({ ...line, balancePaisa: balance })
  }
  return {
    ...range,
    openingPaisa: opening,
    rows,
    billedPaisa: billed,
    paidPaisa: paid,
    closingPaisa: opening + billed - paid,
  }
}

export interface OpenBill {
  id: string
  number: string | null
  date: string
  dueDate: string | null
  totalPaisa: number
  /** Paid and credited. */
  settledPaisa: number
  openPaisa: number
  /** Days past its due date; 0 or less when not late (or no due date). */
  lateDays: number
}

export const AGES = ['0–30 days', '31–60 days', '61–90 days', 'Over 90 days'] as const

export interface CustomerStanding {
  /** Every bill, less every payment and credit note. */
  owedPaisa: number
  /** Payments set against no bill: they lower what is owed and come off the next bill. */
  paidAheadPaisa: number
  openBills: OpenBill[]
  latePaisa: number
  lateBills: number
  /** The most days any open bill is past its due date. */
  mostLateDays: number
  oldestOpen: OpenBill | null
  /** Open money by how old its bill is (`AGES`). */
  ageing: [number, number, number, number]
}

/** The numbers at the top of the customer's page, as of `today`. */
export function customerStanding(ledger: CustomerLedger, today: string): CustomerStanding {
  const openBills: OpenBill[] = []
  for (const b of ledger.bills) {
    const settled = b.paidPaisa + b.creditedPaisa
    const open = b.totalPaisa - settled
    if (open <= 0) continue
    openBills.push({
      id: b.id,
      number: b.number,
      date: day(b.date),
      dueDate: b.dueDate ? day(b.dueDate) : null,
      totalPaisa: b.totalPaisa,
      settledPaisa: settled,
      openPaisa: open,
      lateDays: b.dueDate ? daysBetween(day(b.dueDate), today) : 0,
    })
  }
  openBills.sort((a, b) => a.date.localeCompare(b.date))
  const late = openBills.filter((b) => b.lateDays > 0)
  const ageing: [number, number, number, number] = [0, 0, 0, 0]
  for (const b of openBills) {
    const age = daysBetween(b.date, today)
    ageing[age <= 30 ? 0 : age <= 60 ? 1 : age <= 90 ? 2 : 3] += b.openPaisa
  }
  const sum = (xs: number[]) => xs.reduce((a, x) => a + x, 0)
  return {
    owedPaisa:
      sum(ledger.bills.map((b) => b.totalPaisa)) -
      sum(ledger.credits.map((c) => c.totalPaisa)) -
      sum(ledger.payments.map((p) => p.amountPaisa)),
    paidAheadPaisa: sum(ledger.payments.map(paidAheadOf)),
    openBills,
    latePaisa: sum(late.map((b) => b.openPaisa)),
    lateBills: late.length,
    mostLateDays: Math.max(0, ...late.map((b) => b.lateDays)),
    oldestOpen: openBills[0] ?? null,
    ageing,
  }
}

const METHOD_WORDS: Record<PaymentMethod, string> = {
  cash: 'cash',
  bank: 'bank transfer',
  card: 'card',
  wallet: 'wallet',
  cheque: 'cheque',
  other: 'other',
}

/** "Cash", "Bank transfer": a payment's method on its own. */
export function methodLabel(method: PaymentMethod): string {
  const words = METHOD_WORDS[method]
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** "Bill", "Credit note", "Payment · cash". */
export function rowWhat(row: Pick<StatementRow, 'kind' | 'method'>): string {
  if (row.kind === 'bill') return 'Bill'
  if (row.kind === 'credit') return 'Credit note'
  return row.method ? `Payment · ${METHOD_WORDS[row.method]}` : 'Payment'
}

/** "1234.50": rupees with two decimals, no grouping or symbol, for a spreadsheet. */
export function plainRupees(paisa: number): string {
  const sign = paisa < 0 ? '-' : ''
  const abs = Math.abs(paisa)
  return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/**
 * The statement as CSV: a BOM so Excel reads Nepali names as UTF-8, CRLF line ends, dates in
 * AD and BS, plain numbers.
 */
export function statementCsv(statement: Statement): string {
  const bs = (iso: string) => {
    try {
      return formatBsDate(adToBs(iso), 'iso')
    } catch {
      return ''
    }
  }
  const head = ['Date (AD)', 'Date (BS)', 'What', 'Number', 'Billed', 'Paid', 'Balance']
  const rows: string[][] = [
    head,
    [
      statement.from,
      bs(statement.from),
      'Balance before this period',
      '',
      '',
      '',
      plainRupees(statement.openingPaisa),
    ],
  ]
  for (const r of statement.rows) {
    const number = r.kind === 'payment' ? (r.reference ?? '') : (r.number ?? r.invoiceNumber ?? '')
    rows.push([
      r.date,
      bs(r.date),
      r.kind === 'credit' && r.invoiceNumber ? `Credit note on ${r.invoiceNumber}` : rowWhat(r),
      number,
      r.billedPaisa ? plainRupees(r.billedPaisa) : '',
      r.paidPaisa ? plainRupees(r.paidPaisa) : '',
      plainRupees(r.balancePaisa),
    ])
  }
  return '﻿' + rows.map((r) => r.map(csvField).join(',')).join('\r\n') + '\r\n'
}

/** "Statement-Shrestha-Traders-2083-84": letters (any script), digits and dashes only. */
export function statementFileName(customerName: string, period: string): string {
  const clean = (s: string) =>
    s
      .normalize('NFC')
      .replace(/[^\p{L}\p{M}\p{N}]+/gu, '-')
      .replace(/^-+|-+$/g, '')
  return ['Statement', clean(customerName), clean(period)].filter(Boolean).join('-')
}

/**
 * Splits `count` table rows over pages that hold `first` rows on page 1 and `later` after.
 * `tail` is how many rows' worth of room the last part (open bills, ageing, how to pay)
 * needs: it goes under the last rows when it fits, else on a page of its own.
 */
export function paginate(
  count: number,
  first: number,
  later: number,
  tail: number,
): { pages: { start: number; end: number }[]; tailOnOwnPage: boolean } {
  const pages: { start: number; end: number }[] = []
  let start = 0
  let room = first
  do {
    const end = Math.min(count, start + room)
    pages.push({ start, end })
    if (end === count) {
      room -= end - start
      break
    }
    start = end
    room = later
  } while (start < count)
  return { pages, tailOnOwnPage: room < tail }
}
